import { buildAppleLoginRequestBody } from '../login/appleSignIn';
import { buildGoogleLoginRequestBody } from '../login/googleSignIn';
import type { LoginOption } from '../login/loginConstants';
import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';
import {
  assetCreateShowsRegionField,
  resolveIsraelDefaultRegionId,
} from './assetCreateFormDefaults';
import {
  assetOwnerPayoutMandatoryKeys,
  assetOwnerPayoutCountryFromCountryId,
  normalizeAssetOwnerPayoutInPostBody,
} from './assetOwnerPayoutBank';
export { EMPTY_ASSET_OWNER_PAYOUT_BANK } from './assetOwnerPayoutBank';

/** Optional on asset-owner register — omit from POST when empty. */
export const ASSET_OWNER_OPTIONAL_REGISTER_FIELDS = [
  'phone_number_2',
  'business_type',
  'state_id',
] as const;

function coerceNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Asset-owner register shows district/state only for the United States. */
export function assetOwnerRegisterShowsRegionField(countryId: unknown): boolean {
  return assetCreateShowsRegionField(countryId);
}

/** Wire defaults applied on register submit when fields are omitted from the UI. */
export function applyAssetOwnerRegisterSubmitDefaults(
  body: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[] = [],
): Record<string, unknown> {
  const next = { ...body };
  if (!assetOwnerRegisterShowsRegionField(next.country_id)) {
    const currentRegion = coerceNumericId(next.state_id);
    if (currentRegion === null) {
      const defaultRegionId = resolveIsraelDefaultRegionId(regions);
      if (defaultRegionId !== null) {
        next.state_id = defaultRegionId;
      }
    }
  }
  return next;
}

function isEmptyRegisterValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  return false;
}

function stripEmptyAssetOwnerOptionalRegisterFields(out: Record<string, unknown>): void {
  for (const key of ASSET_OWNER_OPTIONAL_REGISTER_FIELDS) {
    if (isEmptyRegisterValue(out[key])) {
      delete out[key];
    }
  }
}

export function isAssetOwnerRegisterFlow(selectedOption: string): boolean {
  return selectedOption === 'asset owner';
}

/** Visible register fields — is_business is always true and hidden from the form. */
export function assetOwnerRegisterVisibleData(
  payload: Record<string, unknown>,
  options?: { omitPasswordFields?: boolean },
): Record<string, unknown> {
  const out = { ...payload };
  delete out.is_business;
  delete out.logo;
  delete out.logo_url;
  delete out.payout_country;
  delete out.account_holder_name;
  delete out.bank_number;
  delete out.branch_number;
  delete out.account_number;
  delete out.routing_number;
  delete out.account_type;
  delete out.payout_transfer_day;
  delete out.bank_account;
  delete out.address;
  if (!assetOwnerRegisterShowsRegionField(payload.country_id)) {
    delete out.state_id;
  }
  if (options?.omitPasswordFields) {
    delete out.password;
    delete out.confirm_password;
  }
  return out;
}

export function assetOwnerRegisterMandatoryKeys(
  countryId?: unknown,
): readonly string[] {
  const payoutCountry = assetOwnerPayoutCountryFromCountryId(countryId);
  return [
    'business_name',
    'business_id',
    'contact_first_name',
    'contact_last_name',
    'email',
    'phone_number',
    'password',
    'confirm_password',
    'country_id',
    ...assetOwnerPayoutMandatoryKeys(payoutCountry),
  ] as const;
}

/** First missing required root field key, or null when the form can submit. */
export function missingAssetOwnerRegisterRequiredField(
  payload: Record<string, unknown>,
  options?: { skipPassword?: boolean },
): string | null {
  for (const key of assetOwnerRegisterMandatoryKeys(payload.country_id)) {
    if (
      options?.skipPassword &&
      (key === 'password' || key === 'confirm_password')
    ) {
      continue;
    }
    if (isEmptyRegisterValue(payload[key])) {
      return key;
    }
  }
  return null;
}

/** POST /register/AssetOwner — contact_* names; password omitted when registering via Google/Apple. */
export function prepareAssetOwnerRegisterPostBody(
  payload: Record<string, unknown>,
  googleCredential?: string | null,
  loginType: LoginOption = 'asset owner',
  regions: readonly GeneralInfoPicklistItem[] = [],
  appleCredential?: string | null,
): Record<string, unknown> {
  const out: Record<string, unknown> = applyAssetOwnerRegisterSubmitDefaults(payload, regions);
  delete out.confirm_password;

  const contactFirst = pickString(out.contact_first_name);
  const contactLast = pickString(out.contact_last_name);
  if (contactFirst) {
    out.contact_first_name = contactFirst;
    out.first_name = contactFirst;
  }
  if (contactLast) {
    out.contact_last_name = contactLast;
    out.last_name = contactLast;
  }

  out.is_business = true;

  stripEmptyAssetOwnerOptionalRegisterFields(out);

  delete out.address;

  const logoDataUrl = pickString(out.logo);
  const logoUrl = pickString(out.logo_url);
  if (logoDataUrl?.startsWith('data:image/')) {
    out.logo = logoDataUrl;
    delete out.logo_url;
  } else if (logoUrl?.startsWith('https://')) {
    out.logo_url = logoUrl;
    delete out.logo;
  } else {
    delete out.logo;
    delete out.logo_url;
  }

  if (appleCredential?.trim()) {
    const appleBody = buildAppleLoginRequestBody(appleCredential, loginType);
    delete out.password;
    return normalizeAssetOwnerPayoutInPostBody({ ...out, ...appleBody });
  }

  if (googleCredential?.trim()) {
    const googleBody = buildGoogleLoginRequestBody(googleCredential, loginType);
    delete out.password;
    return normalizeAssetOwnerPayoutInPostBody({ ...out, ...googleBody });
  }

  return normalizeAssetOwnerPayoutInPostBody(out);
}

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}
